<template>
  <div
    v-if="ready"
    :key="componentKey"
    class="vue3-marquee flex flex-row h-max w-full relative overflow-x-hidden"
    @focusin="focused = true"
    @focusout="focused = false"
    @mousedown="mouseDownMarquee = true"
    @mouseenter="mouseOverMarquee = true"
    @mouseleave="mouseOverMarquee = false"
    @mouseup="mouseDownMarquee = false"
  >
    <div ref="marqueeOverlayContainer" :aria-hidden="true" class="h-full w-full absolute" />

    <div
      ref="marqueeContent"
      class="flex flex-none flex-row items-center z-10"
      :style="getContentStyle"
    >
      <slot />
    </div>

    <div
      v-if="!animateOnOverflowOnly || (animateOnOverflowOnly && !animateOnOverflowPause)"
      :aria-hidden="true"
      class="flex flex-none flex-row items-center z-10"
      :style="getContentStyle"
    >
      <slot :cloned="true" />
    </div>

    <div
      v-for="key in cloneAmount"
      :key="key"
      :aria-hidden="true"
      class="cloned flex flex-none flex-row items-center z-10"
      :style="getContentStyle"
    >
      <slot :cloned="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
const {
  direction = 'normal',
  pixelsPerSecond = 15,
  delay = 0,
  loop = 0,
  clone = true,
  animateOnOverflowOnly,
  pauseOnHover = true,
  pauseOnClick,
  pause
} = defineProps<{
  direction?: 'normal' | 'reverse'
  pixelsPerSecond?: number
  delay?: number
  loop?: number
  clone?: boolean
  animateOnOverflowOnly?: boolean
  pauseOnHover?: boolean
  pauseOnClick?: boolean
  pause?: boolean
}>()

const cloneAmount = ref(0)
const minWidth = ref('100%')
const minHeight = ref('auto')
const componentKey = ref(0)
const animateOnOverflowPause = ref(true)
const containerWidth = ref(0)
const contentWidth = ref(0)
const mouseOverMarquee = ref(false)
const mouseDownMarquee = ref(false)
const loopCounter = ref(0)
const loopInterval = ref<ReturnType<typeof setInterval> | null>(null)
const ready = ref(false)
const marqueeContent = ref<HTMLDivElement | null>(null)
const marqueeOverlayContainer = ref<HTMLDivElement | null>(null)
const focused = ref(false)
const calculatedDuration = ref(20)

const checkForClone = async () => {
  setInterval(() => {
    minWidth.value = '0%'
    minHeight.value = 'auto'

    if (marqueeContent.value && marqueeOverlayContainer.value) {
      if ('clientWidth' in marqueeContent.value && 'clientWidth' in marqueeOverlayContainer.value) {
        contentWidth.value = marqueeContent.value.clientWidth
        containerWidth.value = marqueeOverlayContainer.value.clientWidth

        if (animateOnOverflowOnly && ready.value) {
          if (contentWidth.value <= containerWidth.value)
            animateOnOverflowPause.value = true
          else
            animateOnOverflowPause.value = false

          return 0
        }

        const localCloneAmount = Math.ceil(containerWidth.value / contentWidth.value)

        cloneAmount.value = Number.isFinite(localCloneAmount) ? localCloneAmount : 0

        if (pixelsPerSecond && contentWidth.value)
          calculatedDuration.value = contentWidth.value / pixelsPerSecond

        return cloneAmount.value
      }
      else {
        minWidth.value = '100%'
        return 0
      }
    }
    else {
      minWidth.value = '100%'
      return 0
    }
  }, 100)
}

const forcesUpdate = async () => {
  await checkForClone()
  componentKey.value++
}

const getAnimationPlayState = computed(() => {
  if (pause || (animateOnOverflowOnly && animateOnOverflowPause.value)) {
    return 'paused'
  }

  if (pauseOnHover && mouseOverMarquee.value) {
    return 'paused'
  }

  if (pauseOnClick && mouseDownMarquee.value) {
    return 'paused'
  }

  return 'running'
})

const getContentStyle = computed(() => {
  const baseStyle = {
    minWidth: minWidth.value,
    minHeight: minHeight.value
  }

  if (focused.value) return baseStyle

  return {
    ...baseStyle,
    animation: `scrollX ${calculatedDuration.value}s linear ${delay}s ${loop === 0 ? 'infinite' : loop} ${direction}`,
    animationPlayState: getAnimationPlayState.value
  }
})

const setupMarquee = async () => {
  minHeight.value = 'auto'

  if (animateOnOverflowOnly)
    minWidth.value = 'auto'
  else
    minWidth.value = '100%'

  if (clone || animateOnOverflowOnly) {
    await checkForClone()
    forcesUpdate()
    ready.value = true
  }
  else {
    ready.value = true
  }
}

watch(contentWidth, () => {
  if (clone) {
    forcesUpdate()
  }

  if (pixelsPerSecond && contentWidth.value) {
    calculatedDuration.value = contentWidth.value / pixelsPerSecond
  }
})

watch(containerWidth, () => {
  if (clone || animateOnOverflowOnly) {
    forcesUpdate()
  }
})

onMounted(() => {
  setupMarquee()

  loopInterval.value = setInterval(() => {
    loopCounter.value++

    if (loop !== 0 && loopCounter.value === loop) {
      if (loopInterval.value) clearInterval(loopInterval.value)
    }
  }, calculatedDuration.value * 1000)
})

onBeforeUnmount(() => {
  if (loopInterval.value) clearInterval(loopInterval.value)
})
</script>
